import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Activity,
  CreditCard,
  FileText,
  Heart,
  Menu,
  Mic,
  Pill,
  Settings,
  Users,
  X,
  Home,
} from 'lucide-react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/context/AuthContext'
import {
  fetchHealthEntries,
} from '@/lib/health'
import type { HealthEntry } from '@/types/health'
import Dashboard from '@/pages/Dashboard'
import DailyCheckIn from '@/pages/DailyCheckIn'
import DoctorAccess from '@/pages/DoctorAccess'
import SecureDoctorView from '@/pages/SecureDoctorView'
import Invoices from '@/pages/Invoices'
import LabResults from '@/pages/LabResults'
import Login from '@/pages/Login'
import MedicalDocuments from '@/pages/MedicalDocuments'
import SettingsPage from '@/pages/Settings'
import Treatments from '@/pages/Treatments'

const menuItems = [
  { icon: Home, label: 'Tableau de bord', path: '/' },
  { icon: Mic, label: 'Journal quotidien', path: '/journal' },
  { icon: FileText, label: 'Documents medicaux', path: '/documents' },
  { icon: CreditCard, label: 'Factures', path: '/invoices' },
  { icon: Pill, label: 'Traitements', path: '/treatments' },
  { icon: Activity, label: 'Analyses biologiques', path: '/lab-results' },
] as const

function Navigation({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()

  const handleNavClick = (path: string) => {
    navigate(path)
    if (window.innerWidth < 1024) {
      onClose()
    }
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <aside
        className={`fixed left-0 top-0 h-full w-72 bg-dark-surface border-r border-dark-border z-50 transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 lg:z-auto lg:shrink-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col overflow-y-auto">
          <div className="p-6 border-b border-dark-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-500 to-cyan-500 flex items-center justify-center shadow-glow-sm">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">MedTrack Pro</h1>
                  <p className="text-xs text-slate-400">Suivi sante Camille</p>
                </div>
              </div>
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-dark-surface-high"
                onClick={onClose}
                type="button"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          <nav className="px-4 pb-24 space-y-1 flex-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const active = location.pathname === item.path

              return (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  type="button"
                  className={`w-full rounded-xl px-4 py-3 transition-all flex items-center gap-3 ${
                    active
                      ? 'bg-primary-900/35 text-primary-300 border border-primary-800/40'
                      : 'text-slate-400 hover:text-white hover:bg-dark-surface-high'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </aside>
    </>
  )
}

function Header({
  onMenuClick,
  latestEntry,
  patientName,
}: {
  onMenuClick: () => void
  latestEntry: HealthEntry | null
  patientName: string
}) {
  const navigate = useNavigate()

  return (
    <header className="sticky top-0 z-30 border-b border-dark-border bg-dark-surface/95 backdrop-blur">
      <div className="px-4 lg:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-dark-surface-high"
              onClick={onMenuClick}
              type="button"
            >
              <Menu className="w-5 h-5 text-slate-300" />
            </button>

            <div>
              <h2 className="text-lg font-semibold text-white">Bonjour {patientName}</h2>
              <p className="text-sm text-slate-400">
                {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {latestEntry?.urgent_alert && (
              <div className="rounded-lg border border-red-800/40 bg-red-900/20 px-3 py-2 text-sm text-red-200">
                Alerte
              </div>
            )}

            <button
              className="hidden sm:inline-flex items-center gap-2 rounded-lg border border-dark-border bg-dark-surface-high px-3 py-2 text-sm text-slate-300 transition-colors hover:text-white"
              onClick={() => navigate('/settings#doctors')}
              type="button"
            >
              <Users className="w-4 h-4" />
              Medecins
            </button>

            <button
              className="inline-flex items-center justify-center rounded-lg border border-dark-border bg-dark-surface-high p-2 text-slate-300 transition-colors hover:text-white"
              onClick={() => navigate('/settings')}
              type="button"
              aria-label="Parametres"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

function AppShell() {
  const { loading, user, profile } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const metadataRole = user?.user_metadata?.role
  const effectiveRole =
    profile?.role ||
    (metadataRole === 'doctor' || metadataRole === 'admin' || metadataRole === 'patient'
      ? metadataRole
      : null)

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 1024)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const latestEntryQuery = useQuery({
    queryKey: ['health-entries', user?.id, 'latest'],
    queryFn: async () => {
      const entries = await fetchHealthEntries(user!.id, 1)
      return entries[0] ?? null
    },
    enabled: Boolean(user),
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (effectiveRole === 'doctor' || effectiveRole === 'admin') {
    return <Navigate to="/doctor" replace />
  }

  const latestEntry = latestEntryQuery.data ?? null
  const patientName = profile?.full_name?.split(' ')[0] ?? 'Camille'

  return (
    <div className="min-h-screen bg-dark-bg flex">
      <Navigation
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onMenuClick={() => setSidebarOpen((current) => !current)}
          latestEntry={latestEntry}
          patientName={patientName}
        />

        <main className="flex-1 overflow-y-auto px-4 lg:px-6 py-6 space-y-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/voice" element={<Navigate to="/journal" replace />} />
            <Route path="/journal" element={<DailyCheckIn />} />
            <Route path="/documents" element={<MedicalDocuments />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/treatments" element={<Treatments />} />
            <Route path="/lab-results" element={<LabResults />} />
            <Route path="/analysis" element={<Navigate to="/" replace />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/doctor-access" element={<DoctorAccess />} />
      <Route path="/doctor" element={<SecureDoctorView />} />
      <Route path="/*" element={<AppShell />} />
    </Routes>
  )
}
